import { useMemo, useState } from 'react';

import { useSelectionParam } from '../../hooks/navigation/useUrlState';
import {
  useAffinityListConfigs,
  useArchiveCampaign,
  useCampaigns,
  useDeleteCampaign,
  useDuplicateCampaign,
  useUnarchiveCampaign,
} from '../../hooks/useOutreach';
import type { Campaign, CampaignStatus } from '../../types/outreach';
import { formatEtDate } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';
import { CampaignCreate } from './CampaignCreate';
import { CampaignDetail } from './CampaignDetail';
import { RefreshButton } from './RefreshButton';


/**
 * Campaigns sub-view: list + detail + create. Detail is rendered inline
 * (not a route -- the outreach engine doesn't have its own router).
 *
 * Archived campaigns appear in a separate, collapsed-by-default group
 * below the active list. Archive is a soft signal -- archived
 * campaigns still exist on the backend; sequencer / Affinity behavior
 * is unaffected. From the archived group, admins can unarchive (back
 * to the active list) or hard-delete (with confirm).
 */
export function CampaignsAdmin() {
  const campaignsQ = useCampaigns();
  const listsQ = useAffinityListConfigs();
  const [creating, setCreating] = useState(false);
  // Open campaign is a shareable, reload-stable deep link via `?campaign=<id>`
  // (push — Back returns to the list). The wrapper keeps the existing
  // setOpenCampaignId(id|null) call sites unchanged.
  const { value: campaignParam, open: openCampaign, close: closeCampaign } =
    useSelectionParam('campaign');
  const openCampaignId =
    campaignParam != null && /^\d+$/.test(campaignParam) ? parseInt(campaignParam, 10) : null;
  const setOpenCampaignId = (id: number | null) => {
    if (id == null) closeCampaign();
    else openCampaign(String(id));
  };
  // After a fresh create, land the user on the Steps tab with a
  // blank Step 1 editor pre-opened (replaces the inline Step-1
  // template that used to live on the create page).
  const [justCreated, setJustCreated] = useState(false);
  const [archivedExpanded, setArchivedExpanded] = useState(false);

  // Build a quick lookup so the List column can show the friendly
  // label ("AGM 2026") instead of the raw affinity_list_id (346659).
  // Falls back to the id when the list config hasn't loaded yet or
  // the id isn't in the registered set.
  const listLabelById = new Map<number, string>();
  for (const l of listsQ.data || []) {
    listLabelById.set(l.affinity_list_id, l.label || l.list_slug);
  }

  // Split active vs archived. Backend returns both in one call; we
  // partition here so the active table stays tight and archived
  // collapses by default. Memoised so reordering on every render is
  // avoided when the user toggles the archived section.
  const { active, archived } = useMemo(() => {
    const all = campaignsQ.data || [];
    const a: Campaign[] = [];
    const ar: Campaign[] = [];
    for (const c of all) {
      (c.archived_at ? ar : a).push(c);
    }
    return { active: a, archived: ar };
  }, [campaignsQ.data]);

  if (creating) {
    return (
      <CampaignCreate
        onClose={(newId) => {
          setCreating(false);
          if (newId) {
            setJustCreated(true);
            setOpenCampaignId(newId);
          }
        }}
      />
    );
  }

  if (openCampaignId != null) {
    return (
      <CampaignDetail
        campaignId={openCampaignId}
        onClose={() => {
          setOpenCampaignId(null);
          setJustCreated(false);
        }}
        initialSubTab={justCreated ? 'steps' : undefined}
        autoOpenAddStep={justCreated}
      />
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Campaigns</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RefreshButton
            onClick={() => campaignsQ.refetch()}
            isFetching={campaignsQ.isFetching}
            title="Re-fetch campaigns list"
          />
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={() => setCreating(true)}
          >
            + New campaign
          </button>
        </div>
      </div>

      {campaignsQ.isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : (campaignsQ.data?.length ?? 0) === 0 ? (
        <div className={styles.empty}>
          No campaigns yet. Create one to get started.
        </div>
      ) : active.length === 0 && archived.length > 0 ? (
        <div className={styles.empty} style={{ marginBottom: 16 }}>
          No active campaigns. {archived.length} archived below.
        </div>
      ) : (
        <CampaignsTable
          rows={active}
          listLabelById={listLabelById}
          onOpen={setOpenCampaignId}
          archivedView={false}
        />
      )}

      {archived.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <button
            type="button"
            onClick={() => setArchivedExpanded((v) => !v)}
            style={{
              background: 'none',
              border: 0,
              padding: '6px 0',
              cursor: 'pointer',
              color: '#666',
              fontSize: 13,
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            title={
              archivedExpanded
                ? 'Hide archived campaigns'
                : 'Show archived campaigns'
            }
          >
            <span style={{ fontSize: 10, lineHeight: 1 }}>
              {archivedExpanded ? '▼' : '▶'}
            </span>
            Archived ({archived.length})
          </button>
          {archivedExpanded && (
            <div style={{ marginTop: 8, opacity: 0.85 }}>
              <CampaignsTable
                rows={archived}
                listLabelById={listLabelById}
                onOpen={setOpenCampaignId}
                archivedView={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function CampaignsTable({
  rows,
  listLabelById,
  onOpen,
  archivedView,
}: {
  rows: Campaign[];
  listLabelById: Map<number, string>;
  onOpen: (id: number) => void;
  archivedView: boolean;
}) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Status</th>
          <th>Sender</th>
          <th>List</th>
          <th>Steps</th>
          <th
            title="Total recipients enrolled on this campaign"
            style={{ textAlign: 'right' }}
          >Recipients</th>
          <th
            title="Recipients who have received at least one sent email on this campaign (excludes test sends)"
            style={{ textAlign: 'right' }}
          >Sent</th>
          <th
            title="Recipients with at least one estimated tracked image load"
            style={{ textAlign: 'right' }}
          >Opened</th>
          <th
            title="Recipients with at least one tracked link click"
            style={{ textAlign: 'right' }}
          >Clicked</th>
          <th
            title="Recipients with a reply attributed to them"
            style={{ textAlign: 'right' }}
          >Replied</th>
          <th
            title="Recipients whose list status maps to this campaign's RSVP-attending or RSVP-virtual event"
            style={{ textAlign: 'right' }}
          >RSVP'd</th>
          <th
            title="Whether Event Details (calendar invite metadata) is set on this campaign"
            style={{ textAlign: 'center' }}
          >Event</th>
          <th
            title="Pending RSVP approval queue rows awaiting operator action"
            style={{ textAlign: 'right' }}
          >Queue</th>
          <th>{archivedView ? 'Archived' : 'Created'}</th>
          <th style={{ width: 1 }} />
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <CampaignRow
            key={c.id}
            campaign={c}
            listLabelById={listLabelById}
            onOpen={onOpen}
            archivedView={archivedView}
          />
        ))}
      </tbody>
    </table>
  );
}


function CampaignRow({
  campaign: c,
  listLabelById,
  onOpen,
  archivedView,
}: {
  campaign: Campaign;
  listLabelById: Map<number, string>;
  onOpen: (id: number) => void;
  archivedView: boolean;
}) {
  const archiveMut = useArchiveCampaign();
  const unarchiveMut = useUnarchiveCampaign();
  const deleteMut = useDeleteCampaign();
  const duplicateMut = useDuplicateCampaign();

  // Active campaigns can't be archived (backend returns 409). Disable
  // the button + tooltip rather than letting the click surface an
  // error toast -- the constraint is stable, so the UI should reflect
  // it pre-emptively.
  const canArchive = c.status !== 'active';
  const archiveTitle = canArchive
    ? 'Move to the archived section. Reversible — sequencer + status write-back behavior unchanged.'
    : 'Active campaigns cannot be archived. Pause or finish first.';

  async function handleArchive() {
    try {
      await archiveMut.mutateAsync(c.id);
    } catch (e: unknown) {
      alert(`Archive failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  async function handleUnarchive() {
    try {
      await unarchiveMut.mutateAsync(c.id);
    } catch (e: unknown) {
      alert(`Unarchive failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  async function handleDuplicate() {
    try {
      const clone = await duplicateMut.mutateAsync(c.id);
      // If the source had event details, the clone has them too but
      // with `Send calendar invites` toggled OFF as a safety default.
      // Surface that so the operator knows to flip it on when ready.
      if (c.event_starts_at != null) {
        alert(
          'Duplicated. Event details were copied, but Send calendar ' +
          'invites is OFF on the duplicate. Turn it on from the ' +
          'Overview tab when the clone is ready to fire calendar invites.',
        );
      }
      // Open the clone immediately so the operator can edit + enroll
      // without an extra Campaigns-list scan.
      onOpen(clone.id);
    } catch (e: unknown) {
      alert(`Duplicate failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  async function handleDelete() {
    // Two-step confirm — same pattern as bulk-clear-recipients. The
    // hard-delete is irreversible (campaign + steps + recipients +
    // sends + replies all cascade), so the second prompt forces a
    // beat to reconsider.
    if (
      !window.confirm(
        `Permanently delete "${c.name}"?\n\n` +
          'This removes the campaign, its steps, recipients, sends, and ' +
          'reply history. List status fields are NOT cleared. ' +
          'This cannot be undone.',
      )
    ) {
      return;
    }
    if (!window.confirm('Are you sure? This cannot be undone.')) {
      return;
    }
    try {
      await deleteMut.mutateAsync(c.id);
    } catch (e: unknown) {
      alert(`Delete failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  return (
    <tr>
      <td>
        <button
          type="button"
          onClick={() => onOpen(c.id)}
          style={{
            background: 'none', border: 0, padding: 0,
            color: '#1a4f8a', cursor: 'pointer', font: 'inherit',
            textDecoration: 'underline', fontWeight: 500,
          }}
        >
          {c.name}
        </button>
      </td>
      <td>
        <StatusBadge status={c.status} />
      </td>
      <td className={styles.code}>{c.sender_email}</td>
      <td>
        {listLabelById.get(c.affinity_list_id) || (
          <span className={styles.code}>{c.affinity_list_id}</span>
        )}
      </td>
      <td>{c.steps.length}</td>
      <td style={{ textAlign: 'right' }}>{c.recipient_count}</td>
      <td style={{ textAlign: 'right' }}>{c.sent_count}</td>
      <td style={{ textAlign: 'right' }}>{c.opened_count}</td>
      <td style={{ textAlign: 'right' }}>{c.clicked_count}</td>
      <td style={{ textAlign: 'right' }}>{c.replied_count}</td>
      <td style={{ textAlign: 'right' }}>{c.rsvp_yes_count}</td>
      <td style={{ textAlign: 'center' }}>
        <EventConfiguredIndicator campaign={c} />
      </td>
      <td style={{ textAlign: 'right' }}>
        <QueueBadge count={c.pending_rsvp_count} />
      </td>
      <td style={{ fontSize: 12 }}>
        {archivedView && c.archived_at
          ? formatEtDate(c.archived_at)
          : formatEtDate(c.created_at)}
      </td>
      <td>
        {/* Subtle row actions — bordered link-style buttons rather
            than the page's primary CTA, since this is a per-row
            secondary action surface. Whitespace nowrap so a
            two-button group doesn't break across the column. */}
        <div
          style={{
            display: 'inline-flex',
            gap: 6,
            whiteSpace: 'nowrap',
          }}
        >
          {!archivedView ? (
            <>
              <button
                type="button"
                onClick={handleDuplicate}
                disabled={duplicateMut.isPending}
                title="Clone this campaign as a fresh draft (steps copied; no recipients enrolled)."
                style={{
                  background: 'none',
                  border: 0,
                  padding: '2px 6px',
                  fontSize: 11,
                  color: '#666',
                  cursor: 'pointer',
                  fontFamily: "'DM Mono', monospace",
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {duplicateMut.isPending ? 'duplicating…' : 'duplicate'}
              </button>
              <button
                type="button"
                onClick={handleArchive}
                disabled={!canArchive || archiveMut.isPending}
                title={archiveTitle}
                style={{
                  background: 'none',
                  border: 0,
                  padding: '2px 6px',
                  fontSize: 11,
                  color: canArchive ? '#666' : '#bbb',
                  cursor: canArchive ? 'pointer' : 'not-allowed',
                  fontFamily: "'DM Mono', monospace",
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {archiveMut.isPending ? 'archiving…' : 'archive'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleUnarchive}
                disabled={unarchiveMut.isPending}
                title="Restore to the active campaigns list."
                style={{
                  background: 'none',
                  border: 0,
                  padding: '2px 6px',
                  fontSize: 11,
                  color: '#666',
                  cursor: 'pointer',
                  fontFamily: "'DM Mono', monospace",
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {unarchiveMut.isPending ? 'restoring…' : 'unarchive'}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMut.isPending}
                title="Permanently delete the campaign + all related data."
                style={{
                  background: 'none',
                  border: 0,
                  padding: '2px 6px',
                  fontSize: 11,
                  color: '#B04030',
                  cursor: 'pointer',
                  fontFamily: "'DM Mono', monospace",
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {deleteMut.isPending ? 'deleting…' : 'delete'}
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}


function EventConfiguredIndicator({ campaign }: { campaign: Campaign }) {
  const set =
    campaign.event_starts_at != null && campaign.event_ends_at != null;
  if (set) {
    return (
      <span
        title="Event Details set — RSVP queue rows with send-invite can be approved"
        style={{ color: '#2E6E2E', fontSize: 14 }}
      >
        ✓
      </span>
    );
  }
  return (
    <span
      title="Event Details not configured — send-invite approvals will refuse until set"
      style={{ color: '#B04030', fontSize: 11 }}
    >
      not set
    </span>
  );
}


function QueueBadge({ count }: { count: number }) {
  if (count === 0) {
    return <span style={{ color: '#999', fontSize: 13 }}>0</span>;
  }
  return (
    <span
      title={`${count} pending RSVP approval row${count === 1 ? '' : 's'} awaiting operator action`}
      style={{
        display: 'inline-block',
        minWidth: 22,
        padding: '1px 8px',
        borderRadius: 10,
        background: '#FFF7E0',
        color: '#7B5400',
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "'DM Mono', monospace",
      }}
    >
      {count}
    </span>
  );
}


function StatusBadge({ status }: { status: CampaignStatus }) {
  const colorMap: Record<CampaignStatus, { bg: string; fg: string }> = {
    draft:    { bg: '#F0F0EC', fg: '#666' },
    active:   { bg: '#E5F4E5', fg: '#2E6E2E' },
    paused:   { bg: '#FFF7E0', fg: '#7B5400' },
    finished: { bg: '#E5E8F4', fg: '#2A4A8A' },
  };
  const { bg, fg } = colorMap[status] || colorMap.draft;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 10,
        background: bg, color: fg,
        fontSize: 11,
        fontFamily: "'DM Mono', monospace",
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {status}
    </span>
  );
}
